import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import SharedPageLayout from './SharedPageLayout';
import SectionModal from './SectionModal';
import { theme } from '../theme';
import gastronomieData from '../data/gastronomie.json';

const GastronomieMainSection = styled.section`
  width: 100%;
  padding: 20px 5% 80px 5%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 15px 4% 60px 4%;
  }

  @media (max-width: 480px) {
    padding: 10px 3% 40px 3%;
  }
`;

const GastronomieMainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-x: hidden;

  @media (max-width: 768px) {
    gap: 0;
  }

  @media (max-width: 480px) {
    gap: 0;
  }
`;

const SectionTitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-right: 50px;

  @media (max-width: 480px) {
    padding-right: 45px;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 32px;
  line-height: 40px;
  color: ${theme.colors.outchou_blue};
  text-align: center;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 36px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const ScrollIndicator = styled.div`
  display: none;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  pointer-events: none;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    display: flex;
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    right: 6px;
  }

  svg {
    width: 100%;
    height: 100%;
    stroke: ${theme.colors.outchou_blue};
  }
`;

const SectionDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.gray};
  text-align: justify;
  margin: 0 0 40px 0;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 21px;
  }
`;

const CategoriesCarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 30px;
  margin-bottom: 0;
  padding: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    margin-top: 15px;
  }

  .swiper {
    padding-right: 60px;
    overflow: visible;
    
    @media (max-width: 768px) {
      padding-right: 0;
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    display: none;
  }
`;

const CategoryNavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 10px;
  margin-bottom: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CategoryNavButton = styled.button<{ $disabled?: boolean }>`
  background: transparent;
  color: ${theme.colors.outchou_green};
  border: none;
  width: 80px;
  height: 80px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  font-weight: 400;
  transition: opacity 0.2s ease;
  padding: 0;
  opacity: ${props => props.$disabled ? 0.2 : 1};

  &:hover {
    opacity: ${props => props.$disabled ? 0.2 : 0.7};
  }

  &:active {
    opacity: ${props => props.$disabled ? 0.2 : 0.5};
  }
`;

const SwiperSlideStyled = styled(SwiperSlide)`
  width: auto;
  height: auto;
`;

const CategoryCarouselButton = styled.button<{ $direction: 'left' | 'right' }>`
  position: relative;
  flex-shrink: 0;
  background: white;
  border: 2px solid ${theme.colors.outchou_green};
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 24px;
  color: ${theme.colors.outchou_green};
  z-index: 10;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${theme.colors.outchou_green};
    color: white;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

const CategoryCard = styled.div<{ $isSelected: boolean }>`
  background: transparent;
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  transform: ${props => props.$isSelected ? 'scale(1.02)' : 'scale(1)'};
  width: 280px;
  position: relative;
  flex-shrink: 0;

  &:hover {
    transform: ${props => props.$isSelected ? 'scale(1.02)' : 'scale(1.01)'};
  }

  @media (min-width: 480px) {
    width: 300px;
  }

  @media (min-width: 768px) {
    width: 380px;
  }
`;

const CategoryImage = styled.div<{ imageUrl: string; $categoryColor?: string }>`
  width: 100%;
  height: 400px;
  background-image: url(${props => props.imageUrl}), linear-gradient(135deg, ${props => props.$categoryColor || theme.colors.outchou_green} 0%, ${props => props.$categoryColor ? `${props.$categoryColor}CC` : theme.colors.outchou_blue} 100%);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0) 100%);
    z-index: 1;
  }

  @media (max-width: 768px) {
    height: 350px;
    padding: 24px;
  }

  @media (max-width: 480px) {
    height: 300px;
    padding: 20px;
  }
`;

const CategoryContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
`;

const CategoryTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 32px;
  color: white;
  margin: 0 0 8px 0;
  text-align: left;
  letter-spacing: -0.5px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 26px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

const CategoryDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  text-align: left;
  line-height: 1.5;
  margin: 0 0 12px 0;
  font-weight: 300;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const CategoryCount = styled.span`
  font-family: ${theme.fonts.body};
  font-size: 12px;
  color: white;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 6px 12px;
  border-radius: 0;
  display: inline-block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const DishesSection = styled.div`
  margin-top: 0;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  position: relative;
`;

const DishesTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 24px;
  color: ${theme.colors.outchou_blue};
  margin: 0 0 20px 0;
  text-align: center;
`;

const DishesCarouselWrapper = styled.div<{ $categoryColor?: string }>`
  position: relative;
  width: 100%;
  padding: 0;
  margin-bottom: 0;
  overflow: hidden;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 0;
  }

  .swiper {
    padding-right: 60px;
    overflow: visible;
    
    @media (max-width: 768px) {
      padding-right: 0;
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    display: none;
  }
`;

const DishesNavigationButtons = styled.div<{ $categoryColor?: string }>`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 10px;
  margin-bottom: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DishNavButton = styled.button<{ $disabled?: boolean; $categoryColor?: string }>`
  background: transparent;
  color: ${props => props.$categoryColor || theme.colors.outchou_green};
  border: none;
  width: 80px;
  height: 80px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  font-weight: 400;
  transition: opacity 0.2s ease;
  padding: 0;
  opacity: ${props => props.$disabled ? 0.2 : 1};

  &:hover {
    opacity: ${props => props.$disabled ? 0.2 : 0.7};
  }

  &:active {
    opacity: ${props => props.$disabled ? 0.2 : 0.5};
  }
`;

const DishImage = styled.div<{ imageUrl: string; $categoryColor?: string }>`
  width: 250px;
  height: 280px;
  background-image: url(${props => props.imageUrl}), linear-gradient(135deg, ${props => props.$categoryColor || theme.colors.outchou_green} 0%, ${props => props.$categoryColor ? `${props.$categoryColor}CC` : theme.colors.outchou_blue} 100%);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    width: 300px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0) 100%);
    z-index: 1;
  }

  @media (max-width: 768px) {
    height: 240px;
    padding: 16px;
  }
`;

const DishContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
`;

const DishName = styled.h4`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 20px;
  color: white;
  margin: 0;
  text-align: left;
  letter-spacing: -0.3px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;


const CarouselControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 20px;
  margin-bottom: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CarouselButton = styled.button<{ $categoryColor?: string }>`
  background: transparent;
  color: ${props => props.$categoryColor || theme.colors.outchou_green};
  border: none;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 400;
  transition: opacity 0.2s ease;
  padding: 0;

  &:hover {
    opacity: 0.7;
  }

  &:active {
    opacity: 0.5;
  }

  &:disabled {
    opacity: 0.2;
    cursor: not-allowed;
  }
`;

const CategoryNavigation = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  opacity: 0.7;
`;

const CategoryNavItem = styled.button<{ isActive: boolean }>`
  background: none;
  color: ${props => props.isActive ? theme.colors.outchou_blue : theme.colors.gray};
  border: none;
  border-bottom: ${props => props.isActive ? `1px solid ${theme.colors.outchou_green}` : '1px solid transparent'};
  padding: 4px 0;
  font-size: 11px;
  font-weight: ${props => props.isActive ? '500' : '300'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    color: ${props => props.isActive ? theme.colors.outchou_blue : theme.colors.outchou_green};
    border-bottom-color: ${props => props.isActive ? theme.colors.outchou_green : theme.colors.outchou_green};
    opacity: 1;
  }
`;

const SubcategoriesTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-size: 20px;
  font-weight: 600;
  color: ${theme.colors.outchou_blue};
  margin: 30px 0 15px 0;
  text-align: center;
`;

const CategoryDropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 10001;
  opacity: 1 !important;
`;

const CategoryDropdownButton = styled.button<{ $isOpen: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${theme.fonts.body};
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.outchou_blue};
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  svg {
    transition: transform 0.2s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const SubcategoriesDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: #ffffff !important;
  border: 1px solid ${theme.colors.outchou_blue};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  min-width: 200px;
  z-index: 10000;
  opacity: ${props => props.$isOpen ? 1 : 0} !important;
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.3s ease;
  margin-top: 8px;
  padding: 8px 0;
  max-height: 300px;
  overflow-y: auto;

  @media (max-width: 768px) {
    min-width: 180px;
  }

  @media (max-width: 480px) {
    min-width: 160px;
  }
`;

const SubcategoryLink = styled.button<{ $isActive: boolean }>`
  background: none;
  border: none;
  padding: 8px 16px;
  cursor: pointer;
  font-family: ${theme.fonts.body};
  font-size: 14px;
  font-weight: ${props => props.$isActive ? '500' : '400'};
  color: ${props => props.$isActive ? theme.colors.outchou_blue : theme.colors.gray};
  text-decoration: none;
  transition: all 0.2s ease;
  width: 100%;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background: ${theme.colors.outchou_blue}10;
    color: ${theme.colors.outchou_blue};
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 6px 12px;
  }
`;

const SubcategoryCount = styled.span`
  font-size: 12px;
  opacity: 0.6;
  margin-left: 8px;
`;

// Fonction pour obtenir la couleur d'une catégorie de plat
const getCategoryColor = (categoryId: string): string => {
  const colorMap: { [key: string]: string } = {
    'entrees': theme.colors.outchou_green,           // Vert pour les entrées
    'soupes': '#E07B39',                             // Orange pour les soupes
    'plats': theme.colors.outchou_blue,             // Bleu pour les plats
    'pain-galette': '#D4A017',                       // Or pour le pain
    'desserts': '#C77DFF',                          // Violet pour les desserts
    'patisseries': '#FF6B9D',                        // Rose pour les pâtisseries
    'fromages': '#8B9A46',                          // Vert olive pour les fromages
    'boissons': '#4A90E2',                          // Bleu clair pour les boissons
  };
  
  return colorMap[categoryId] || theme.colors.outchou_green;
};

// Fonction pour parser la description d'un plat en sections
const parseDishDescription = (description: string): { [key: string]: string[] | string } => {
  const sections: { [key: string]: string[] | string } = {};
  
  // Patterns pour extraire les sections (ordre important - les plus spécifiques en premier)
  const patterns = [
    { key: 'Description & Fabrication', regex: /Description\s*&\s*Fabrication\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Détails techniques', regex: /Détails techniques\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Particularités régionales', regex: /Particularités régionales\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Autres noms', regex: /Autres noms\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Étymologie', regex: /Étymologie\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Héritage', regex: /Héritage\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Origine', regex: /Origine\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Description générale', regex: /Description générale\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Description', regex: /Description\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Fabrication', regex: /Fabrication\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Particularité', regex: /Particularité\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Caractère', regex: /Caractère\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Usage', regex: /Usage\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Occasions', regex: /Occasions\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Variantes', regex: /Variantes\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
    { key: 'Histoire', regex: /Histoire\s*:\s*([^\.]+(?:\.[^A-Z][^\.]*)*)/i },
  ];
  
  // Extraire les sections avec patterns
  let remainingDescription = description;
  const foundSections: string[] = [];
  
  patterns.forEach(({ key, regex }) => {
    const match = remainingDescription.match(regex);
    if (match && match[1]) {
      const content = match[1].trim();
      // Ne pas ajouter si c'est vide ou déjà présent
      if (content && !sections[key]) {
        sections[key] = content;
        foundSections.push(match[0]);
      }
    }
  });
  
  // Si aucune section n'a été trouvée, mettre toute la description dans "Description"
  if (Object.keys(sections).length === 0) {
    sections['Description'] = description;
  } else {
    // Extraire le texte restant qui n'a pas été capturé
    let remainingText = description;
    foundSections.forEach(sectionText => {
      remainingText = remainingText.replace(sectionText, '').trim();
    });
    
    // Nettoyer les espaces multiples et points en début/fin
    remainingText = remainingText.replace(/^[\.\s]+|[\.\s]+$/g, '').trim();
    
    // Si il reste du texte et qu'on n'a pas de section "Description", l'ajouter
    if (remainingText && !sections['Description'] && !sections['Description générale']) {
      // Vérifier si le texte restant commence par une majuscule (probablement une nouvelle phrase)
      if (remainingText.length > 0) {
        sections['Description'] = remainingText;
      }
    }
  }
  
  return sections;
};

const DishCard = styled.div`
  width: auto;
  background: transparent;
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: scale(1.01);
  }
`;


const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 0;
  font-family: ${theme.fonts.body};
  font-size: 12px;
  position: relative;
  z-index: 10000;
`;

const BreadcrumbItem = styled.span<{ $isClickable?: boolean }>`
  color: ${props => props.$isClickable ? theme.colors.outchou_blue : theme.colors.gray};
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  font-weight: ${props => props.$isClickable ? '400' : '300'};
  transition: all 0.2s ease;
  opacity: ${props => props.$isClickable ? '1' : '0.6'};
  
  &:hover {
    color: ${props => props.$isClickable ? theme.colors.outchou_green : theme.colors.gray};
    opacity: ${props => props.$isClickable ? '1' : '0.6'};
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${theme.colors.gray};
  font-weight: 300;
  opacity: 0.4;
`;

const CategoryNote = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 14px;
  line-height: 1.5;
  color: ${theme.colors.gray};
  margin: 0 0 16px 0;
  text-align: left;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

interface Dish {
  id: number;
  name: string;
  description?: string;
  image: string;
  sections?: { [key: string]: string };
}

const GastronomiePage: React.FC = () => {
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [selectedDish, setSelectedDish] = useState<Dish | null>(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [isSubcategoryDropdownOpen, setIsSubcategoryDropdownOpen] = useState(false);
  const categorySwiperRef = useRef<SwiperType | null>(null);
  const dishesSwiperRef = useRef<SwiperType | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const categories = gastronomieData.categories;

  // Réinitialiser la sélection quand on arrive sur la page gastronomie
  useEffect(() => {
    if (location.pathname === '/gastronomie') {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSelectedDish(null);
      setIsSubcategoryDropdownOpen(false);
      // Réinitialiser les swipers
      if (categorySwiperRef.current) {
        categorySwiperRef.current.slideTo(0);
      }
      if (dishesSwiperRef.current) {
        dishesSwiperRef.current.slideTo(0);
      }
    }
  }, [location.pathname]);

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsSubcategoryDropdownOpen(false);
      }
    };

    if (isSubcategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSubcategoryDropdownOpen]);
  
  // Fonction pour obtenir l'URL complète de l'image
  const getImageUrl = (imagePath: string): string => {
    if (!imagePath) return '';
    // En développement, utiliser l'URL complète du backend pour éviter les problèmes de proxy
    if (process.env.NODE_ENV === 'development') {
      return `http://localhost:5001${imagePath}`;
    }
    return imagePath;
  };


  const handleCategoryClick = (categoryId: string) => {
    // Ne pas permettre de désélectionner une catégorie déjà sélectionnée
    if (selectedCategory === categoryId) {
      return;
    }
    setSelectedCategory(categoryId);
    setSelectedSubcategory(null); // Reset subcategory when changing category
    setIsSubcategoryDropdownOpen(false); // Fermer le dropdown
    // Reset Swiper si nécessaire
    if (dishesSwiperRef.current) {
      dishesSwiperRef.current.slideTo(0);
    }
  };

  const handleSubcategoryClick = (subcategoryId: string) => {
    // Ne pas permettre de désélectionner une sous-catégorie déjà sélectionnée
    if (selectedSubcategory === subcategoryId) {
      return;
    }
    setSelectedSubcategory(subcategoryId);
    // Reset Swiper si nécessaire
    if (dishesSwiperRef.current) {
      dishesSwiperRef.current.slideTo(0);
    }
  };

  const getCurrentDishes = () => {
    if (!selectedCategory) return [];
    const category = categories.find(cat => cat.id === selectedCategory);
    
    // Si une sous-catégorie est sélectionnée, afficher ses plats
    if (category && 'subcategories' in category && Array.isArray(category.subcategories) && selectedSubcategory) {
      const subcategories = category.subcategories as any[];
      const subcategory = subcategories.find((sub: any) => sub.id === selectedSubcategory);
      return subcategory?.dishes || [];
    }
    
    // Si la catégorie a des sous-catégories mais aucune n'est sélectionnée, afficher tous les plats de toutes les sous-catégories
    if (category && 'subcategories' in category && Array.isArray(category.subcategories)) {
      const subcategories = category.subcategories as any[];
      return subcategories.flatMap((sub: any) => sub.dishes || []);
    }
    
    // Si la catégorie a des plats directs, les afficher
    return category?.dishes || [];
  };

  const getCurrentCategory = () => {
    return categories.find(cat => cat.id === selectedCategory);
  };

  const getDishCategoryId = (dish: Dish): string | null => {
    // Si une catégorie est déjà sélectionnée, l'utiliser directement
    if (selectedCategory) {
      return selectedCategory;
    }
    
    // Sinon, chercher dans toutes les catégories
    for (const category of categories) {
      if ('subcategories' in category && Array.isArray(category.subcategories)) {
        const subcategories = category.subcategories as any[];
        for (const subcategory of subcategories) {
          if (subcategory.dishes?.some((d: any) => d.id === dish.id && d.name === dish.name)) {
            return category.id;
          }
        }
      } else if ('dishes' in category && Array.isArray(category.dishes)) {
        const dishes = category.dishes as any[];
        if (dishes.some((d: any) => d.id === dish.id && d.name === dish.name)) {
          return category.id;
        }
      }
    }
    return null;
  };

  const getCurrentSubcategory = () => {
    if (!selectedCategory || !selectedSubcategory) return null;
    const category = categories.find(cat => cat.id === selectedCategory);
    if (category && 'subcategories' in category && Array.isArray(category.subcategories)) {
      const subcategories = category.subcategories as any[];
      return subcategories.find((sub: any) => sub.id === selectedSubcategory) || null;
    }
    return null;
  };

  const getCategoryDishCount = (category: any) => {
    if ('dishes' in category && Array.isArray(category.dishes)) {
      return category.dishes.length;
    }
    if ('subcategories' in category && Array.isArray(category.subcategories)) {
      const subcategories = category.subcategories as any[];
      return subcategories.reduce((total: number, sub: any) => total + (sub.dishes?.length || 0), 0);
    }
    return 0;
  };

  return (
    <SharedPageLayout 
      heroType="search"
      backgroundImage="/shared_assets/images/navigation/gastronomie.jpeg"
      heroTitle="Gastronomie"
      heroSubtitle="Découvrez la richesse culinaire de l'Algérie à travers ses plats traditionnels, ses saveurs authentiques et ses recettes transmises de génération en génération."
    >
      <GastronomieMainSection>
        <GastronomieMainContent>
          {/* Titre en haut */}
          <div style={{ marginBottom: '0' }}>
            <SectionTitleWrapper>
              <SectionTitle style={{ marginBottom: '0' }}>
                Les saveurs de l'Algérie
              </SectionTitle>
              <ScrollIndicator>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </ScrollIndicator>
            </SectionTitleWrapper>
          </div>

          {/* Section des plats avec carrousel - AFFICHÉE EN PREMIER */}
          {selectedCategory && (
            <DishesSection>
              {/* Breadcrumb pour la navigation */}
              <Breadcrumb>
                <BreadcrumbItem 
                  $isClickable={true}
                  onClick={() => {
                    setSelectedCategory(null);
                    setSelectedSubcategory(null);
                    setIsSubcategoryDropdownOpen(false);
                  }}
                >
                  Toutes les catégories
                </BreadcrumbItem>
                <BreadcrumbSeparator>›</BreadcrumbSeparator>
                {getCurrentCategory()?.subcategories ? (
                  <CategoryDropdownContainer ref={dropdownRef}>
                    <CategoryDropdownButton
                      $isOpen={isSubcategoryDropdownOpen}
                      onClick={() => setIsSubcategoryDropdownOpen(!isSubcategoryDropdownOpen)}
                    >
                      {selectedSubcategory ? getCurrentSubcategory()?.name : getCurrentCategory()?.name}
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </CategoryDropdownButton>
                    <SubcategoriesDropdown $isOpen={isSubcategoryDropdownOpen}>
                      <SubcategoryLink
                        $isActive={selectedSubcategory === null}
                        onClick={() => {
                          setSelectedSubcategory(null);
                          setIsSubcategoryDropdownOpen(false);
                        }}
                      >
                        Tous
                        <SubcategoryCount>({getCategoryDishCount(getCurrentCategory()!)})</SubcategoryCount>
                      </SubcategoryLink>
                      {getCurrentCategory()?.subcategories?.map((subcategory) => (
                        <SubcategoryLink
                          key={subcategory.id}
                          $isActive={selectedSubcategory === subcategory.id}
                          onClick={() => {
                            handleSubcategoryClick(subcategory.id);
                            setIsSubcategoryDropdownOpen(false);
                          }}
                        >
                          {subcategory.name}
                          <SubcategoryCount>({subcategory.dishes.length})</SubcategoryCount>
                        </SubcategoryLink>
                      ))}
                    </SubcategoriesDropdown>
                  </CategoryDropdownContainer>
                ) : (
                  <BreadcrumbItem $isClickable={false}>
                    {getCurrentCategory()?.name}
                  </BreadcrumbItem>
                )}
              </Breadcrumb>

              {selectedCategory === 'entrees' && (
                <CategoryNote>
                  ⚠️ À noter : le mot « bourek » n'existe pas à l'Ouest de l'Algérie, où l'on utilise le terme brick. Le pliage est souvent carré ou triangulaire. Les boureks, hérités des Turcs, désignent plutôt des rouleaux.
                </CategoryNote>
              )}
              
              <DishesCarouselWrapper $categoryColor={selectedCategory ? getCategoryColor(selectedCategory) : undefined}>
                <Swiper
                  modules={[Navigation, FreeMode]}
                  spaceBetween={20}
                  slidesPerView="auto"
                  freeMode={true}
                  navigation={false}
                  onSwiper={(swiper: SwiperType) => {
                    dishesSwiperRef.current = swiper;
                    setUpdateTrigger(prev => prev + 1);
                  }}
                  onSlideChange={() => {
                    setUpdateTrigger(prev => prev + 1);
                  }}
                >
                  {getCurrentDishes().map((dish: Dish, index: number) => (
                    <SwiperSlide key={`${selectedCategory}-${selectedSubcategory || 'all'}-${dish.id}-${dish.name}-${index}`} style={{ width: 'auto' }}>
                      <DishCard onClick={() => setSelectedDish(dish)}>
                        <DishImage imageUrl={getImageUrl(dish.image)} $categoryColor={getCategoryColor(getDishCategoryId(dish) || '')}>
                          <DishContent>
                            <DishName>{dish.name}</DishName>
                          </DishContent>
                        </DishImage>
                      </DishCard>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <DishesNavigationButtons $categoryColor={selectedCategory ? getCategoryColor(selectedCategory) : undefined}>
                  <DishNavButton
                    $categoryColor={selectedCategory ? getCategoryColor(selectedCategory) : undefined}
                    $disabled={!dishesSwiperRef.current || dishesSwiperRef.current.isBeginning}
                    onClick={() => dishesSwiperRef.current?.slidePrev()}
                  >
                    ‹
                  </DishNavButton>
                  <DishNavButton
                    $categoryColor={selectedCategory ? getCategoryColor(selectedCategory) : undefined}
                    $disabled={!dishesSwiperRef.current || dishesSwiperRef.current.isEnd}
                    onClick={() => dishesSwiperRef.current?.slideNext()}
                  >
                    ›
                  </DishNavButton>
                </DishesNavigationButtons>
              </DishesCarouselWrapper>
            </DishesSection>
          )}

          {/* Afficher le carrousel de catégories seulement si aucune catégorie n'est sélectionnée */}
          {!selectedCategory && (
            <CategoriesCarouselWrapper>
              <Swiper
                modules={[Navigation, FreeMode]}
                spaceBetween={20}
                slidesPerView="auto"
                freeMode={true}
                navigation={false}
                onSwiper={(swiper: SwiperType) => {
                  categorySwiperRef.current = swiper;
                  setUpdateTrigger(prev => prev + 1);
                }}
                onSlideChange={() => {
                  setUpdateTrigger(prev => prev + 1);
                }}
              >
                {categories.map((category) => (
                  <SwiperSlide key={category.id} style={{ width: 'auto' }}>
                    <CategoryCard
                      $isSelected={false}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      <CategoryImage imageUrl={getImageUrl(category.image)} $categoryColor={getCategoryColor(category.id)}>
                        <CategoryContent>
                          <CategoryTitle>{category.name}</CategoryTitle>
                          <CategoryDescription>{category.description}</CategoryDescription>
                          <CategoryCount>
                            {getCategoryDishCount(category)} spécialité{getCategoryDishCount(category) > 1 ? 's' : ''}
                          </CategoryCount>
                        </CategoryContent>
                      </CategoryImage>
                    </CategoryCard>
                  </SwiperSlide>
                ))}
              </Swiper>
              <CategoryNavigationButtons>
                <CategoryNavButton
                  $disabled={!categorySwiperRef.current || categorySwiperRef.current.isBeginning}
                  onClick={() => categorySwiperRef.current?.slidePrev()}
                >
                  ‹
                </CategoryNavButton>
                <CategoryNavButton
                  $disabled={!categorySwiperRef.current || categorySwiperRef.current.isEnd}
                  onClick={() => categorySwiperRef.current?.slideNext()}
                >
                  ›
                </CategoryNavButton>
              </CategoryNavigationButtons>
            </CategoriesCarouselWrapper>
          )}
        </GastronomieMainContent>
      </GastronomieMainSection>

      {/* Modal pour afficher les détails d'un plat */}
      <SectionModal
        title={selectedDish?.name || ''}
        imageUrl={selectedDish?.image}
        sections={selectedDish ? (selectedDish.sections || parseDishDescription(selectedDish.description || '')) : {}}
        color={selectedDish ? getCategoryColor(getDishCategoryId(selectedDish) || '') : theme.colors.outchou_green}
        isOpen={!!selectedDish}
        onClose={() => setSelectedDish(null)}
        defaultExpandedSections={['Description', 'Description générale']}
      />
    </SharedPageLayout>
  );
};

export default GastronomiePage;